import {
  ApolloCache,
  ApolloClient,
  gql,
  QueryHookOptions,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import {
  GET_PARTNER_SETTINGS_QUERY,
  GetPartnerSettingValueQuery,
  PartnerSetting,
  usePartnerSettings,
} from 'src/graphql';
import {
  excludePlatformWhenDenied,
  usePlatformCatalogAccess,
} from 'src/graphql/entities/group-rules/platform-catalog-access';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { BehaviorRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { mapBehaviorResponseToUI, mapFormValuesToBehaviorRequest } from './behavior.mappings';
import { RulesFormModel, UIBehavior } from './behavior.types';

const BEHAVIOR_FIELDS = gql`
  fragment BehaviorFields on Behavior {
    id
    behaviorId
    partnerId
    behaviorTrigger
    conditions
    actions
    createdAt
    updatedAt
    name
    description
  }
`;

const BEHAVIOR_RELATIONS = gql`
  fragment BehaviorRelations on Behavior {
    id
    sequences {
      id
      sequencePosition
    }
  }
`;

const GET_SORTED_BEHAVIORS_QUERY = gql`
  query GetSortedBehaviors($filters: [ConditionInput!]) @entity(name: "Behavior") {
    getSortedBehaviors(filters: $filters) {
      records {
        ...BehaviorFields
        ...BehaviorRelations
      }
    }
  }
  ${BEHAVIOR_FIELDS}
  ${BEHAVIOR_RELATIONS}
`;

interface GetBehaviorsQuery {
  getSortedBehaviors: Records<BehaviorRaw>;
}

const CREATE_BEHAVIOR_MUTATION = gql`
  mutation CreateBehavior($params: BehaviorDataInput!) {
    createBehavior(data: $params) {
      ...BehaviorFields
      ...BehaviorRelations
    }
  }
  ${BEHAVIOR_FIELDS}
  ${BEHAVIOR_RELATIONS}
`;

const UPDATE_BEHAVIOR_MUTATION = gql`
  mutation UpdateBehavior($behaviorId: String!, $params: BehaviorDataInput!) {
    updateBehavior(
      conditions: [{ attribute: "behaviorId", operator: "=", value: $behaviorId }]
      data: $params
    ) {
      ...BehaviorFields
    }
  }
  ${BEHAVIOR_FIELDS}
`;

const DELETE_BEHAVIOR_MUTATION = gql`
  mutation DeleteBehavior($behaviorId: String!) {
    deleteBehavior(
      conditions: [{ attribute: "behaviorId", operator: "=", value: $behaviorId }]
    ) {
      ...BehaviorFields
    }
  }
  ${BEHAVIOR_FIELDS}
`;

const DISABLE_BEHAVIOR_MUTATION = gql`
  mutation DisableBehavior($behaviorId: String!, $partnerId: Int!) {
    disableBehavior(params: { behaviorId: $behaviorId, partnerId: $partnerId }) {
      behaviorId
      success
    }
  }
`;

const ENABLE_BEHAVIOR_MUTATION = gql`
  mutation EnableBehavior($behaviorId: String!, $partnerId: Int!) {
    enableBehavior(params: { behaviorId: $behaviorId, partnerId: $partnerId }) {
      behaviorId
      success
    }
  }
`;

const UPDATE_BEHAVIOR_SORTING_MUTATION = gql`
  mutation UpdateBehaviorSorting($index: Int!, $behaviorId: String!) {
    updateBehaviorSorting(params: { index: $index, behaviorId: $behaviorId }) {
      success
    }
  }
`;

export const useBehaviors = (
  options?: QueryHookOptions<GetBehaviorsQuery> & { filters?: ConditionInput[] },
) => {
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { includePlatformLevelData: includePlatformBehaviors } = usePlatformCatalogAccess(
    'Behavior',
    selectedPartnerId,
  );

  const { data: disabledBehaviorIds } = usePartnerSettings({
    partnerSetting: PartnerSetting.DisabledBehaviors,
    partnerId: selectedPartnerId,
    splitPartnerAndPlatformData: true,
  });

  const {
    data: behaviorsData,
    loading: behaviorsDataLoading,
    client,
    variables,
  } = useQuery<GetBehaviorsQuery>(GET_SORTED_BEHAVIORS_QUERY, {
    variables: {
      ...options?.variables,
      filters: options?.filters ?? [],
    },
    ...options,
    context: {
      includePlatformLevelData: includePlatformBehaviors,
    },
  });

  const behaviors = useMemo(
    () =>
      (behaviorsData?.getSortedBehaviors?.records || [])
        .map((item) => mapBehaviorResponseToUI(item))
        .filter(excludePlatformWhenDenied(includePlatformBehaviors))
        // Partners should not be able to see platform-level disabled behaviors
        .filter(
          (behavior) =>
            behavior.partnerId === selectedPartnerId ||
            !disabledBehaviorIds.platformLevel.includes(behavior.id),
        ),
    [
      behaviorsData,
      disabledBehaviorIds.platformLevel,
      selectedPartnerId,
      includePlatformBehaviors,
    ],
  );

  const enabledBehaviors = useMemo(() => {
    return behaviors.filter(
      (behavior) => !disabledBehaviorIds.partnerLevel.includes(behavior.id),
    );
  }, [behaviors, disabledBehaviorIds]);

  const disabledBehaviors = useMemo(() => {
    return behaviors.filter((behavior) =>
      disabledBehaviorIds.partnerLevel.includes(behavior.id),
    );
  }, [behaviors, disabledBehaviorIds]);

  const [createBehaviorMutation, { loading: createBehaviorLoading }] = useMutation(
    CREATE_BEHAVIOR_MUTATION,
    {
      onCompleted: () => {
        showSuccessAlert('Rule created successfully');
      },
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong creating rule, please try again');
      },
    },
  );

  const [updateBehaviorMutation, { loading: updateBehaviorLoading }] = useMutation(
    UPDATE_BEHAVIOR_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong updating rule, please try again');
      },
    },
  );

  const [deleteBehaviorMutation] = useMutation(DELETE_BEHAVIOR_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong deleting rule, please try again');
    },
  });

  const [disableBehaviorMutation, { loading: disableBehaviorLoading }] = useMutation(
    DISABLE_BEHAVIOR_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong, please try again');
      },
    },
  );

  const [enableBehaviorMutation, { loading: enableBehaviorLoading }] = useMutation(
    ENABLE_BEHAVIOR_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong, please try again');
      },
    },
  );

  const [updateBehaviorSortingMutation, { loading: updateBehaviorSortingLoading }] =
    useMutation(UPDATE_BEHAVIOR_SORTING_MUTATION, {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong, please try again');
      },
    });

  const executeBehaviorSaveMutation = useCallback(
    ({ params, partnerId }: { params: Partial<BehaviorRaw>; partnerId: number }) => {
      const createdAt = new Date().toISOString();
      const tempId = `temp-id:${createdAt}`;
      return createBehaviorMutation({
        variables: { params },
        optimisticResponse: {
          createBehavior: [
            {
              __typename: 'Behavior',
              ...params,
              id: tempId,
              behaviorId: tempId,
              partnerId,
              createdAt,
              updatedAt: createdAt,
              sequences: [],
            },
          ],
        },
        update: (
          _: ApolloCache<unknown>,
          response: {
            data?: {
              createBehavior: BehaviorRaw[];
            };
          },
        ) => {
          addNewBehaviorToCache(client, { ...options, variables })(
            response?.data?.createBehavior?.[0],
          );
        },
      });
    },
    [client, options, variables, createBehaviorMutation],
  );

  const createBehavior = useCallback(
    (data: RulesFormModel, partnerId: number) => {
      const params = mapFormValuesToBehaviorRequest(data, partnerId);
      return executeBehaviorSaveMutation({ params, partnerId });
    },
    [executeBehaviorSaveMutation],
  );

  const copyBehavior = useCallback(
    (data: UIBehavior) => {
      const params = {
        name: `Copy of ${data.name}`,
        partnerId: data.partnerId,
        description: data.description,
        behaviorTrigger: data.behaviorTrigger,
        conditions: JSON.stringify(data.conditions),
        actions: JSON.stringify(data.actions),
      };

      return executeBehaviorSaveMutation({ params, partnerId: data.partnerId });
    },
    [executeBehaviorSaveMutation],
  );

  const updateBehavior = useCallback(
    (behaviorId: string, data: RulesFormModel, partnerId: number) => {
      const params = mapFormValuesToBehaviorRequest(data, partnerId);
      return updateBehaviorMutation({
        variables: {
          behaviorId,
          params,
        },
      });
    },
    [updateBehaviorMutation],
  );

  const deleteBehavior = useCallback(
    (behaviorId: string) => {
      return deleteBehaviorMutation({
        variables: { behaviorId },
        optimisticResponse: {
          deleteBehavior: {
            behaviorId,
          },
        },
        update(cache) {
          const id = cache.identify({
            __typename: 'Behavior',
            id: behaviorId,
          });

          cache.evict({ id });
          cache.gc();
        },
      });
    },
    [deleteBehaviorMutation],
  );

  const disableBehavior = useCallback(
    (behaviorId: string) => {
      return disableBehaviorMutation({
        variables: { behaviorId, partnerId: selectedPartnerId },
        optimisticResponse: {
          disableBehavior: {
            behaviorId,
            success: true,
          },
        },
        update(cache) {
          cache.updateQuery<GetPartnerSettingValueQuery, QueryVariables>(
            {
              query: GET_PARTNER_SETTINGS_QUERY,
              variables: {
                params: {
                  filters: [
                    {
                      attribute: 'partnerSettingId',
                      operator: '=',
                      value: PartnerSetting.DisabledBehaviors,
                    },
                  ],
                },
              },
            },
            (data) => {
              if (!data) return data;

              const recordToUpdate = data.getPartnerSettingValue.records.find(
                (record) => record.partnerId === selectedPartnerId,
              );

              if (!recordToUpdate) {
                return {
                  getPartnerSettingValue: {
                    __typename: 'PartnerSettingValueDataResponse',
                    records: [
                      ...data.getPartnerSettingValue.records,
                      {
                        partnerId: selectedPartnerId || 0,
                        value: behaviorId,
                        id: 'new',
                        partnerSettingId: PartnerSetting.DisabledBehaviors,
                        partnerSettingValueId: 'new',
                        updatedAt: new Date().toISOString(),
                        createdAt: new Date().toISOString(),
                      },
                    ],
                  },
                };
              }

              return {
                getPartnerSettingValue: {
                  __typename: 'PartnerSettingValueDataResponse',
                  records: data.getPartnerSettingValue.records.map((record) => {
                    if (
                      record.partnerSettingValueId !== recordToUpdate.partnerSettingValueId
                    ) {
                      return record;
                    }

                    const tempDisabledBehaviors = (record.value || '')
                      .split(',')
                      .filter((item) => item);

                    return {
                      ...record,
                      value: [...tempDisabledBehaviors, behaviorId].join(','),
                    };
                  }),
                },
              };
            },
          );
        },
      });
    },
    [disableBehaviorMutation, selectedPartnerId],
  );

  const enableBehavior = useCallback(
    (behaviorId: string) => {
      return enableBehaviorMutation({
        variables: { behaviorId, partnerId: selectedPartnerId },
        optimisticResponse: {
          enableBehavior: {
            behaviorId,
            success: true,
          },
        },
        update(cache) {
          cache.updateQuery<GetPartnerSettingValueQuery, QueryVariables>(
            {
              query: GET_PARTNER_SETTINGS_QUERY,
              variables: {
                params: {
                  filters: [
                    {
                      attribute: 'partnerSettingId',
                      operator: '=',
                      value: PartnerSetting.DisabledBehaviors,
                    },
                  ],
                },
              },
            },
            (data) => {
              if (!data) return data;

              const recordToUpdate = data.getPartnerSettingValue.records.find(
                (record) => record.partnerId === selectedPartnerId,
              );

              if (!recordToUpdate) {
                return data;
              }

              return {
                getPartnerSettingValue: {
                  __typename: 'PartnerSettingValueDataResponse',
                  records: data.getPartnerSettingValue.records.map((record) => {
                    if (
                      record.partnerSettingValueId !== recordToUpdate.partnerSettingValueId
                    ) {
                      return record;
                    }

                    const tempDisabledBehaviors = (record.value || '')
                      .split(',')
                      .filter((item) => item && item !== behaviorId);

                    return {
                      ...record,
                      value: tempDisabledBehaviors.join(','),
                    };
                  }),
                },
              };
            },
          );
        },
      });
    },
    [enableBehaviorMutation, selectedPartnerId],
  );

  const updateBehaviorSorting = useCallback(
    (behaviorId: string, index: number) => {
      return updateBehaviorSortingMutation({
        variables: { behaviorId, index },
        optimisticResponse: {
          updateBehaviorSorting: {
            success: true,
          },
        },
        update(cache) {
          cache.updateQuery<GetBehaviorsQuery>(
            {
              query: GET_SORTED_BEHAVIORS_QUERY,
            },
            (data) => {
              if (!data) return data;

              const movedBehavior = data.getSortedBehaviors.records.find(
                (record) => record.behaviorId === behaviorId,
              );

              if (!movedBehavior) return data;

              const sortedBehaviorsWithoutMoved = data.getSortedBehaviors.records.filter(
                (record) => record.behaviorId !== behaviorId,
              );

              const newSortedBehaviorsList = [
                ...sortedBehaviorsWithoutMoved.slice(0, index),
                movedBehavior,
                ...sortedBehaviorsWithoutMoved.slice(index),
              ];

              return {
                getSortedBehaviors: {
                  __typename: 'GetSortedBehaviorsResponse',
                  records: newSortedBehaviorsList,
                },
              };
            },
          );
        },
      });
    },
    [updateBehaviorSortingMutation],
  );

  return {
    behaviors,
    enabledBehaviors,
    disabledBehaviors,
    createBehavior,
    deleteBehavior,
    updateBehavior,
    copyBehavior,
    disableBehavior,
    enableBehavior,
    updateBehaviorSorting,
    loading:
      behaviorsDataLoading ||
      createBehaviorLoading ||
      updateBehaviorLoading ||
      disableBehaviorLoading ||
      enableBehaviorLoading ||
      updateBehaviorSortingLoading,
  };
};

function addNewBehaviorToCache(
  client: ApolloClient<unknown>,
  queryOptions?: QueryHookOptions<GetBehaviorsQuery>,
) {
  return function (newBehavior?: BehaviorRaw) {
    if (!newBehavior) return;

    client.cache.updateQuery<GetBehaviorsQuery>(
      {
        query: GET_SORTED_BEHAVIORS_QUERY,
        ...queryOptions,
      },
      (cache) => {
        if (!cache) return null;

        const {
          getSortedBehaviors: { records },
        } = cache;
        let updatedBehaviorsList = records;

        const existingBehaviorRecord = records.find((record) => record.id === newBehavior.id);
        if (existingBehaviorRecord) {
          updatedBehaviorsList = records.map((behavior) =>
            behavior.id === newBehavior.id ? newBehavior : behavior,
          );
        } else {
          updatedBehaviorsList = [...records, newBehavior];
        }

        return {
          ...cache,
          getSortedBehaviors: {
            ...cache.getSortedBehaviors,
            records: updatedBehaviorsList,
          },
        };
      },
    );
  };
}

export function getCachedBehavior(cache: ApolloCache<unknown>, behaviorId?: string) {
  if (!behaviorId) {
    return;
  }

  const fragment = gql`
    fragment CurrentBehavior on Behavior {
      ...BehaviorFields
    }
    ${BEHAVIOR_FIELDS}
  `;

  const id = cache.identify({
    __typename: 'Behavior',
    id: behaviorId,
  });

  return cache.readFragment<BehaviorRaw>({
    id,
    fragment,
    fragmentName: 'CurrentBehavior',
  });
}
